import { useState, useCallback, useRef, useEffect } from "react";
import { SearchOutlined } from "@ant-design/icons";

/**
 * 快捷搜索 Hook — 封装搜索框的状态、防抖和 filterParams 同步逻辑。
 *
 * 使用方式：
 * ```jsx
 * const quickSearch = useQuickSearch({ setFilterParams, setPagination });
 *
 * <Input
 *     placeholder="搜索账单号/供应商..."
 *     style={{ width: 240, height: 26 }}
 *     {...quickSearch.inputProps}
 * />
 * ```
 *
 * @param {Object} options
 * @param {Function} options.setFilterParams - 页面的 filterParams setter
 * @param {Function} options.setPagination - 页面的 pagination setter
 * @param {string} [options.paramKey='search'] - 搜索参数在 filterParams 中的键名
 * @param {number} [options.debounceMs=500] - 防抖延迟（毫秒）
 * @returns {{ value: string, inputProps: object, clear: Function }}
 */
const useQuickSearch = ({ setFilterParams, setPagination, paramKey = "search", debounceMs = 500, initialValue = "" }) => {
    const [value, setValue] = useState(initialValue);
    const debounceRef = useRef(null);

    // 输入变化 → 防抖 → 更新 filterParams
    const onChange = useCallback(
        (e) => {
            const val = e.target.value;
            setValue(val);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                setFilterParams((prev) => {
                    const next = { ...prev };
                    if (val.trim()) {
                        next[paramKey] = val.trim();
                    } else {
                        delete next[paramKey];
                    }
                    return next;
                });
                setPagination((prev) => ({ ...prev, current: 1 }));
            }, debounceMs);
        },
        [setFilterParams, setPagination, paramKey, debounceMs]
    );

    // 清除搜索
    const clear = useCallback(() => {
        setValue("");
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setFilterParams((prev) => {
            const next = { ...prev };
            delete next[paramKey];
            return next;
        });
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [setFilterParams, setPagination, paramKey]);

    // 组件卸载时清理定时器
    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    // 预组装 Input props，页面只需展开 + 自定义 placeholder/style
    const inputProps = {
        prefix: <SearchOutlined style={{ color: "#bfbfbf" }} />,
        size: "small",
        value,
        onChange,
        allowClear: true,
        onClear: clear,
    };

    return { value, inputProps, clear };
};

export default useQuickSearch;
